import { Component, Input, Output, EventEmitter, ViewChild, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { TabsModule } from 'primeng/tabs';
import { ColorPickerModule } from 'primeng/colorpicker';
import {
  ALL_COLORS,
  NEUTRAL_COLORS,
  CORE_COLORS,
  ACCENT_COLORS,
  PASTEL_COLORS,
  SwatchColor,
  isValidHex,
  hexToRgb,
  rgbToHex,
  getContrastTextColor,
} from './color-palette.constants';

export interface ColorSelectedEvent {
  hex: string | null;
  source: 'swatch' | 'recent' | 'manual' | 'advanced' | 'clear';
}

@Component({
  selector: 'app-color-swatch-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    PopoverModule,
    InputTextModule,
    InputNumberModule,
    DividerModule,
    TooltipModule,
    TabsModule,
    ColorPickerModule,
  ],
  template: `
    <button
      type="button"
      pButton
      class="p-button-text p-button-sm color-picker-trigger"
      [class.color-picker-trigger-active]="active"
      (click)="op.toggle($event)"
      [pTooltip]="tooltipText"
      tooltipPosition="bottom"
      [attr.aria-label]="tooltipText"
    >
      <span class="material-icons color-picker-icon">{{ mode === 'background' ? 'format_color_fill' : mode === 'text' ? 'format_color_text' : 'format_paint' }}</span>
      <span class="color-indicator" [style.background-color]="value || 'transparent'">
        <span *ngIf="mode === 'text'" class="preview-text">A</span>
        <span *ngIf="!value" class="no-color-line"></span>
      </span>
    </button>

    <p-popover
      #op
      [appendTo]="overlayAppendTo"
      [style]="{ width: '380px' }"
      [baseZIndex]="1000"
      (onShow)="onPanelShow()"
      (onHide)="onPanelHide()"
    >
        <div class="color-picker-panel" (keydown)="handleKeyDown($event)">
          <!-- Header with current color preview -->
          <div class="flex items-center justify-between mb-3 pb-3 border-b">
            <div class="flex items-center gap-2">
              <div
                class="current-color-preview"
                [style.background-color]="value || '#ffffff'"
                [style.border]="value ? 'none' : '1px solid #d1d5db'"
              >
                <span *ngIf="mode === 'text'" 
                      class="preview-text-large"
                      [style.color]="value || '#000000'">A</span>
                <span *ngIf="mode === 'background'" 
                      class="preview-bg-chip"
                      [style.background-color]="value || 'transparent'"></span>
              </div>
              <div>
                <div class="text-xs font-semibold text-surface-700">
                  {{ mode === 'text' ? 'Text Color' : mode === 'background' ? 'Highlight' : 'Color' }}
                </div>
                <div class="text-xs text-surface-500">{{ value || 'None' }}</div>
              </div>
            </div>
            <button
              *ngIf="allowClear && value"
              type="button"
              pButton
              icon="pi pi-times"
              class="p-button-text p-button-sm p-button-rounded"
              (click)="clearColor()"
              pTooltip="Clear color"
              tooltipPosition="left"
              aria-label="Clear color"
            ></button>
          </div>

          <!-- Tabs for organization -->
          <p-tabs [value]="activeTab" (valueChange)="onTabValueChange($event)">
            <p-tablist>
              <p-tab value="swatches">Swatches</p-tab>
              <p-tab value="custom">Custom</p-tab>
              <p-tab *ngIf="showAdvanced" value="advanced">Advanced</p-tab>
            </p-tablist>
            <p-tabpanels>
              <!-- Swatches tab -->
              <p-tabpanel value="swatches">
              <!-- Recent colors -->
              <div *ngIf="showRecent && recentColors.length > 0" class="mb-4">
                <div class="text-xs font-semibold text-surface-600 mb-2">Recent Colors</div>
                <div class="swatch-grid swatch-grid-compact">
                  <button
                    *ngFor="let color of recentColors; let i = index"
                    type="button"
                    class="swatch"
                    [class.selected]="value === color.hex"
                    [style.background-color]="color.hex"
                    [attr.data-index]="'recent-' + i"
                    [attr.aria-label]="'Recent color ' + color.hex"
                    (click)="selectColor(color.hex, 'recent')"
                    (keydown.enter)="selectColor(color.hex, 'recent')"
                    (keydown.space)="selectColor(color.hex, 'recent'); $event.preventDefault()"
                    [pTooltip]="panelVisible ? color.hex : ''"
                    [tooltipDisabled]="!panelVisible"
                    tooltipPosition="top"
                  ></button>
                </div>
              </div>

              <p-divider *ngIf="showRecent && recentColors.length > 0"></p-divider>

              <!-- Core colors -->
              <div class="mb-3">
                <div class="text-xs font-semibold text-surface-600 mb-2">Core Colors</div>
                <div class="swatch-grid">
                  <button
                    *ngFor="let color of coreColors; let i = index"
                    type="button"
                    class="swatch"
                    [class.selected]="value === color.hex"
                    [style.background-color]="color.hex"
                    [attr.data-index]="'core-' + i"
                    [attr.aria-label]="color.label"
                    (click)="selectColor(color.hex, 'swatch')"
                    (keydown.enter)="selectColor(color.hex, 'swatch')"
                    (keydown.space)="selectColor(color.hex, 'swatch'); $event.preventDefault()"
                    [pTooltip]="panelVisible ? color.label : ''"
                    [tooltipDisabled]="!panelVisible"
                    tooltipPosition="top"
                  ></button>
                </div>
              </div>

              <!-- Neutral colors -->
              <div class="mb-3">
                <div class="text-xs font-semibold text-surface-600 mb-2">Neutral Colors</div>
                <div class="swatch-grid">
                  <button
                    *ngFor="let color of neutralColors; let i = index"
                    type="button"
                    class="swatch"
                    [class.selected]="value === color.hex"
                    [style.background-color]="color.hex"
                    [attr.data-index]="'neutral-' + i"
                    [attr.aria-label]="color.label"
                    (click)="selectColor(color.hex, 'swatch')"
                    (keydown.enter)="selectColor(color.hex, 'swatch')"
                    (keydown.space)="selectColor(color.hex, 'swatch'); $event.preventDefault()"
                    [pTooltip]="panelVisible ? color.label : ''"
                    [tooltipDisabled]="!panelVisible"
                    tooltipPosition="top"
                  ></button>
                </div>
              </div>

              <!-- Accent colors -->
              <div class="mb-3">
                <div class="text-xs font-semibold text-surface-600 mb-2">Accent Colors</div>
                <div class="swatch-grid swatch-grid-large">
                  <button
                    *ngFor="let color of accentColors; let i = index"
                    type="button"
                    class="swatch"
                    [class.selected]="value === color.hex"
                    [style.background-color]="color.hex"
                    [attr.data-index]="'accent-' + i"
                    [attr.aria-label]="color.label"
                    (click)="selectColor(color.hex, 'swatch')"
                    (keydown.enter)="selectColor(color.hex, 'swatch')"
                    (keydown.space)="selectColor(color.hex, 'swatch'); $event.preventDefault()"
                    [pTooltip]="panelVisible ? color.label : ''"
                    [tooltipDisabled]="!panelVisible"
                    tooltipPosition="top"
                  ></button>
                </div>
              </div>

              <!-- Pastel colors -->
              <div class="mb-2">
                <div class="text-xs font-semibold text-surface-600 mb-2">Pastel Colors</div>
                <div class="swatch-grid">
                  <button
                    *ngFor="let color of pastelColors; let i = index"
                    type="button"
                    class="swatch"
                    [class.selected]="value === color.hex"
                    [style.background-color]="color.hex"
                    [attr.data-index]="'pastel-' + i"
                    [attr.aria-label]="color.label"
                    (click)="selectColor(color.hex, 'swatch')"
                    (keydown.enter)="selectColor(color.hex, 'swatch')"
                    (keydown.space)="selectColor(color.hex, 'swatch'); $event.preventDefault()"
                    [pTooltip]="panelVisible ? color.label : ''"
                    [tooltipDisabled]="!panelVisible"
                    tooltipPosition="top"
                  ></button>
                </div>
              </div>
              </p-tabpanel>

              <!-- Manual input tab -->
              <p-tabpanel value="custom">
              <div class="space-y-4">
                <!-- HEX input -->
                <div>
                  <label for="hex-input" class="block text-xs font-semibold text-surface-700 mb-2">
                    HEX Color
                  </label>
                  <input
                    id="hex-input"
                    type="text"
                    pInputText
                    [(ngModel)]="manualHex"
                    (ngModelChange)="onHexChange($event)"
                    placeholder="#000000"
                    class="w-full"
                    maxlength="7"
                  />
                  <small *ngIf="hexError" class="text-red-500">{{ hexError }}</small>
                </div>

                <!-- RGB inputs -->
                <div>
                  <label class="block text-xs font-semibold text-surface-700 mb-2">RGB Values</label>
                  <div class="grid grid-cols-3 gap-2">
                    <div>
                      <label for="rgb-r" class="block text-xs text-surface-600 mb-1">R</label>
                      <p-inputNumber
                        inputId="rgb-r"
                        [(ngModel)]="manualR"
                        (ngModelChange)="onRgbChange()"
                        [min]="0"
                        [max]="255"
                        [showButtons]="false"
                        class="w-full"
                      ></p-inputNumber>
                    </div>
                    <div>
                      <label for="rgb-g" class="block text-xs text-surface-600 mb-1">G</label>
                      <p-inputNumber
                        inputId="rgb-g"
                        [(ngModel)]="manualG"
                        (ngModelChange)="onRgbChange()"
                        [min]="0"
                        [max]="255"
                        [showButtons]="false"
                        class="w-full"
                      ></p-inputNumber>
                    </div>
                    <div>
                      <label for="rgb-b" class="block text-xs text-surface-600 mb-1">B</label>
                      <p-inputNumber
                        inputId="rgb-b"
                        [(ngModel)]="manualB"
                        (ngModelChange)="onRgbChange()"
                        [min]="0"
                        [max]="255"
                        [showButtons]="false"
                        class="w-full"
                      ></p-inputNumber>
                    </div>
                  </div>
                </div>

                <!-- Manual color preview and apply -->
                <div class="flex items-center justify-between gap-3">
                  <div
                    class="manual-color-preview flex-1"
                    [style.background-color]="manualHex"
                    [style.border]="isValidHex(manualHex) ? 'none' : '1px solid #d1d5db'"
                  ></div>
                  <button
                    type="button"
                    pButton
                    label="Apply"
                    class="p-button-sm"
                    [disabled]="!isValidHex(manualHex)"
                    (click)="applyManualColor()"
                  ></button>
                </div>
              </div>
              </p-tabpanel>

              <!-- Advanced ColorPicker tab -->
              <p-tabpanel value="advanced">
                <div class="space-y-4">
                  <div class="text-xs text-surface-600 mb-3">
                    Use the color picker below for fine-tuned color selection.
                  </div>
                  <div class="flex justify-center">
                    <p-colorPicker
                      [(ngModel)]="advancedColor"
                      (onChange)="onAdvancedColorChange($event)"
                      [inline]="true"
                      [format]="'hex'"
                    ></p-colorPicker>
                  </div>
                  <button
                    type="button"
                    pButton
                    label="Apply Advanced Color"
                    class="w-full p-button-sm"
                    (click)="applyAdvancedColor()"
                  ></button>
                </div>
              </p-tabpanel>
            </p-tabpanels>
          </p-tabs>
        </div>
    </p-popover>
  `,
  styles: [`
    :host {
      display: inline-block;
    }

    .color-picker-trigger {
      position: relative;
      min-width: 2.5rem;
      border-radius: 0.375rem;
      transition: background-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease;
    }

    .color-picker-icon {
      font-size: 1rem;
      line-height: 1;
      width: 1rem;
      min-width: 1rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }

    .color-picker-trigger.color-picker-trigger-active {
      background-color: rgba(37, 99, 235, 0.12);
      box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.35);
      color: #1d4ed8;
    }

    .color-picker-trigger.color-picker-trigger-active:hover {
      background-color: rgba(37, 99, 235, 0.18);
    }

    .color-picker-trigger.color-picker-trigger-active .color-indicator {
      border-color: currentColor;
    }

    .color-indicator {
      position: absolute;
      bottom: 2px;
      left: 50%;
      transform: translateX(-50%);
      width: 20px;
      height: 3px;
      border-radius: 2px;
      border: 1px solid rgba(0, 0, 0, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .preview-text {
      font-size: 8px;
      font-weight: bold;
      line-height: 1;
    }

    .no-color-line {
      position: absolute;
      width: 100%;
      height: 1px;
      background: #dc2626;
      transform: rotate(-45deg);
    }

    .color-picker-panel {
      width: 100%;
      max-height: 500px;
      overflow-y: auto;
    }

    .current-color-preview {
      width: 48px;
      height: 48px;
      border-radius: 0.375rem;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .preview-text-large {
      font-size: 24px;
      font-weight: bold;
    }

    .preview-bg-chip {
      width: 32px;
      height: 16px;
      border-radius: 3px;
      border: 1px solid rgba(0, 0, 0, 0.1);
    }

    .swatch-grid {
      display: grid;
      grid-template-columns: repeat(12, 1fr);
      gap: 4px;
    }

    .swatch-grid-compact {
      grid-template-columns: repeat(10, 1fr);
    }

    .swatch-grid-large {
      grid-template-columns: repeat(14, 1fr);
    }

    .swatch {
      width: 100%;
      aspect-ratio: 1;
      border: 2px solid transparent;
      border-radius: 0.25rem;
      cursor: pointer;
      transition: all 0.15s ease;
      position: relative;
    }

    .swatch:hover {
      transform: scale(1.15);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      z-index: 1;
      border-color: rgba(0, 0, 0, 0.2);
    }

    .swatch:focus {
      outline: none;
      transform: scale(1.15);
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5);
      z-index: 1;
    }

    .swatch.selected {
      border-color: #2563eb;
      box-shadow: 0 0 0 2px #2563eb;
    }

    .swatch.selected::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 12px;
      font-weight: bold;
      text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
    }

    .manual-color-preview {
      height: 48px;
      border-radius: 0.375rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    /* Ensure PrimeNG tabs look good */
    ::ng-deep .p-tabs .p-tablist {
      background: transparent;
      border-bottom: 1px solid #e5e7eb;
    }

    ::ng-deep .p-tabs .p-tabpanels {
      padding: 1rem 0;
      background: transparent;
    }
  `],
})
export class ColorSwatchPickerComponent implements OnInit {
  @Input() value: string | null = null;
  @Input() active = false;
  @Input() mode: 'text' | 'background' | 'generic' = 'generic';
  @Input() allowClear = true;
  @Input() showRecent = true;
  @Input() showAdvanced = true;
  @Input() overlayAppendTo: 'body' | HTMLElement | null = 'body';

  @Output() valueChange = new EventEmitter<string | null>();
  @Output() colorSelected = new EventEmitter<ColorSelectedEvent>();

  @ViewChild('op') overlayPanel!: Popover;

  // Color palettes
  neutralColors = NEUTRAL_COLORS;
  coreColors = CORE_COLORS;
  accentColors = ACCENT_COLORS;
  pastelColors = PASTEL_COLORS;

  // Recent colors (in-memory storage)
  recentColors: SwatchColor[] = [];
  private readonly MAX_RECENT_COLORS = 16;

  // Manual input state
  manualHex = '';
  manualR = 0;
  manualG = 0;
  manualB = 0;
  hexError = '';

  // Advanced ColorPicker state
  advancedColor = '';

  // Keep tab state controlled so we can default to Swatches on open.
  activeTab: 'swatches' | 'custom' | 'advanced' = 'swatches';
  panelVisible = false;

  // Keyboard navigation
  private focusedSwatchIndex = -1;

  get tooltipText(): string {
    if (this.mode === 'text') return 'Text color';
    if (this.mode === 'background') return 'Highlight color';
    return 'Color picker';
  }

  ngOnInit(): void {
    this.initializeManualInputs();
    this.loadRecentColors();
  }

  onPanelShow(): void {
    this.panelVisible = true;
    this.initializeManualInputs();
    this.advancedColor = this.value || '#000000';
    this.activeTab = 'swatches';
  }

  onPanelHide(): void {
    this.panelVisible = false;
    this.focusedSwatchIndex = -1;

    // Force-clear any active hover/focus tooltip anchors to avoid lingering tooltip text.
    const active = document.activeElement as HTMLElement | null;
    if (active && typeof active.blur === 'function') {
      active.blur();
    }

    const hovered = document.querySelectorAll('.color-picker-panel .swatch:hover');
    hovered.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
      }
    });
  }

  onTabValueChange(value: string | number | undefined): void {
    if (value === 'swatches' || value === 'custom' || value === 'advanced') {
      this.activeTab = value;
    }
  }

  private initializeManualInputs(): void {
    if (this.value && isValidHex(this.value)) {
      this.manualHex = this.value;
      const rgb = hexToRgb(this.value);
      if (rgb) {
        this.manualR = rgb.r;
        this.manualG = rgb.g;
        this.manualB = rgb.b;
      }
    } else {
      this.manualHex = '#000000';
      this.manualR = 0;
      this.manualG = 0;
      this.manualB = 0;
    }
    this.hexError = '';
  }

  selectColor(hex: string, source: 'swatch' | 'recent'): void {
    this.value = hex;
    this.valueChange.emit(hex);
    this.colorSelected.emit({ hex, source });
    this.addToRecent(hex);
    this.overlayPanel.hide();
  }

  clearColor(): void {
    this.value = null;
    this.valueChange.emit(null);
    this.colorSelected.emit({ hex: null, source: 'clear' });
    this.overlayPanel.hide();
  }

  onHexChange(hex: string): void {
    this.hexError = '';
    if (hex && !hex.startsWith('#')) {
      hex = '#' + hex;
      this.manualHex = hex;
    }

    if (isValidHex(hex)) {
      const rgb = hexToRgb(hex);
      if (rgb) {
        this.manualR = rgb.r;
        this.manualG = rgb.g;
        this.manualB = rgb.b;
      }
    } else if (hex.length >= 7) {
      this.hexError = 'Invalid HEX format';
    }
  }

  onRgbChange(): void {
    const r = Math.max(0, Math.min(255, this.manualR || 0));
    const g = Math.max(0, Math.min(255, this.manualG || 0));
    const b = Math.max(0, Math.min(255, this.manualB || 0));

    this.manualHex = rgbToHex(r, g, b);
    this.hexError = '';
  }

  applyManualColor(): void {
    if (isValidHex(this.manualHex)) {
      this.value = this.manualHex;
      this.valueChange.emit(this.manualHex);
      this.colorSelected.emit({ hex: this.manualHex, source: 'manual' });
      this.addToRecent(this.manualHex);
      this.overlayPanel.hide();
    }
  }

  onAdvancedColorChange(event: any): void {
    // PrimeNG ColorPicker may return the value in different formats
    this.advancedColor = event.value || event;
  }

  applyAdvancedColor(): void {
    let hex = this.advancedColor;
    if (hex && !hex.startsWith('#')) {
      hex = '#' + hex;
    }

    if (isValidHex(hex)) {
      this.value = hex;
      this.valueChange.emit(hex);
      this.colorSelected.emit({ hex, source: 'advanced' });
      this.addToRecent(hex);
      this.overlayPanel.hide();
    }
  }

  isValidHex(hex: string): boolean {
    return isValidHex(hex);
  }

  // Recent colors management
  private addToRecent(hex: string): void {
    // Remove if already exists
    this.recentColors = this.recentColors.filter(c => c.hex !== hex);

    // Add to front
    this.recentColors.unshift({
      label: hex,
      hex,
      category: 'recent',
    });

    // Trim to max size
    if (this.recentColors.length > this.MAX_RECENT_COLORS) {
      this.recentColors = this.recentColors.slice(0, this.MAX_RECENT_COLORS);
    }

    this.saveRecentColors();
  }

  private loadRecentColors(): void {
    try {
      const stored = localStorage.getItem('color-picker-recent');
      if (stored) {
        this.recentColors = JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Failed to load recent colors:', e);
    }
  }

  private saveRecentColors(): void {
    try {
      localStorage.setItem('color-picker-recent', JSON.stringify(this.recentColors));
    } catch (e) {
      console.warn('Failed to save recent colors:', e);
    }
  }

  // Keyboard navigation
  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.overlayPanel.hide();
      return;
    }

    // Arrow key navigation would go here
    // This is a simplified implementation
  }
}
